import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyCheckoutKey, ShopifyCollectKey, ShopifyInventoryQuantityKey } from "./keys";
import { REMOTE_IDENTIFICATION_KEYS_FOR_MODEL, getIdentificationKeysForModel } from "./ShopifyRemoteIdFieldKeys";
import type { ShopifyIdentifyByRemoteKeys } from "./ShopifyRemoteIdFieldKeys";

/**
 * Version-gated identifyByRemoteKeys for models whose alt ID fields change across API versions.
 * Extracted from ShopifyModelMetadata to avoid circular dependency with ShopifyDominion.
 */
export const checkoutIdentifyByRemoteKeys = (shopifyApiVersion: ShopifyApiVersion): ShopifyIdentifyByRemoteKeys | undefined =>
  shopifyApiVersion >= "2026-04" ? REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCheckoutKey] : (["id"] as const);

export const collectIdentifyByRemoteKeys = (shopifyApiVersion: ShopifyApiVersion): ShopifyIdentifyByRemoteKeys | undefined =>
  shopifyApiVersion >= "2025-04" ? REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyCollectKey] : undefined;

export const inventoryQuantityIdentifyByRemoteKeys = (shopifyApiVersion: ShopifyApiVersion): ShopifyIdentifyByRemoteKeys | undefined =>
  shopifyApiVersion >= "2026-01" ? (["id"] as const) : REMOTE_IDENTIFICATION_KEYS_FOR_MODEL[ShopifyInventoryQuantityKey];

/**
 * Get version-aware identifyByRemoteKeys for a model, falling back to the static map.
 */
export const getVersionAwareIdentificationKeys = (modelKey: string, apiVersion: ShopifyApiVersion): readonly string[] | null => {
  const thunks: Record<string, (v: ShopifyApiVersion) => ShopifyIdentifyByRemoteKeys | undefined> = {
    [ShopifyCheckoutKey]: checkoutIdentifyByRemoteKeys,
    [ShopifyCollectKey]: collectIdentifyByRemoteKeys,
    [ShopifyInventoryQuantityKey]: inventoryQuantityIdentifyByRemoteKeys,
  };

  const thunk = thunks[modelKey];
  if (thunk) {
    return thunk(apiVersion) ?? null;
  }

  return getIdentificationKeysForModel(modelKey);
};
